import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const forYou = [
  '希望學習是一種陪伴，而不是壓力',
  '願意為自己保留一段安靜、專屬的時間',
  '對東方音樂美學有共鳴',
  '完全沒有音樂基礎，但想慢慢開始',
  '成人初學者，想為生活添加安靜的角落',
  '家長希望孩子接觸音樂，但不希望過度競爭',
];

const notForYou = [
  '正在追求快速考級或比賽名次',
  '希望短時間內達到演出水準',
  '需要高強度密集訓練的學習者',
  '對競賽成果有強烈期望',
];

export default function AudienceSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">For You</span>
            <div className="divider-gold"></div>
          </div>
          <h2
            className="font-serif-tc text-[#2c2420] leading-[1.4]"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
          >
            這裡適合什麼樣的人？
          </h2>
          <p className="mt-4 text-[1.1875rem] leading-[1.75] text-[#7a6e62] max-w-xl mx-auto">
            認識自己適不適合，是開始學習最重要的第一步。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* For You */}
          <div
            className={`bg-[#f5f0e8] rounded-2xl p-8 md:p-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.1s' }}
          >
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#4a6741]/15">
                <i className="ri-checkbox-circle-line text-[#4a6741] text-xl"></i>
              </div>
              <h3 className="font-serif-tc text-[#2c2420]" style={{ fontSize: '1.375rem' }}>
                適合你的人
              </h3>
            </div>
            <ul className="space-y-4">
              {forYou.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-leaf-line text-[#4a6741] text-base"></i>
                  </div>
                  <p className="text-[1.1875rem] leading-[1.75] text-[#4a3e32]">{item}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Not For You */}
          <div
            className={`rounded-2xl p-8 md:p-10 transition-all duration-700 bg-[#f7f3ee]`}
            style={{ transitionDelay: '0.2s' }}
          >
            <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-7">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#9a8470]/15">
                  <i className="ri-information-line text-[#9a8470] text-xl"></i>
                </div>
                <h3 className="font-serif-tc text-[#2c2420]" style={{ fontSize: '1.375rem' }}>
                  也許暫時不適合的人
                </h3>
              </div>
              <p className="text-[1rem] leading-[1.75] text-[#7a6e62] mb-6">
                以下這些情況，這裡可能不是最適合你的地方。但如果你的想法有所轉變，隨時歡迎回來聊聊。
              </p>
              <ul className="space-y-4">
                {notForYou.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-subtract-line text-[#b0a090] text-base"></i>
                    </div>
                    <p className="text-[1.1875rem] leading-[1.75] text-[#7a6e62]">{item}</p>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-8 border-t border-[#e0d8cc]">
                <p className="text-[1rem] leading-[1.75] text-[#7a6e62]">
                  不確定自己是否適合？
                </p>
                <Link
                  to="/teaching"
                  className="inline-flex items-center gap-2 mt-3 text-[1rem] font-medium text-[#7a6248] hover:text-[#5a4a3a] transition-colors cursor-pointer"
                >
                  了解更多教學方式
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
